'use client';

import React from 'react';

import styled from '@emotion/styled';

import { useBreakpointHighlight } from './ui.video-player-breakpoint.hooks';
import { Breakpoint } from './ui.video-player.types';

interface TimelineHighlightProps {
  selectedBreakpointId: string | null;
  breakpoints: Breakpoint[];
  duration: number;
  timelineHeight: number;
}

const HighlightBar = styled.div<{ left: number; width: number }>`
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  transform: translateZ(0);
`;

const Separator = styled.div<{ left: number; height: number }>`
  left: ${(props) => props.left}%;
  height: ${(props) => props.height}px;
`;

export default function TimelineHighlight({
  selectedBreakpointId,
  breakpoints,
  duration,
  timelineHeight,
}: TimelineHighlightProps) {
  // Use shared hook for highlight calculations
  const highlight = useBreakpointHighlight(
    selectedBreakpointId,
    breakpoints,
    duration
  );

  if (!highlight) return null;

  return (
    <>
      {/* Disabled overlay for areas outside selected breakpoint */}
      <div
        data-test='timeline-overlay'
        className='absolute inset-0 rounded-md bg-gray-500'
      />

      <HighlightBar
        data-test='timeline-highlight'
        className='absolute top-0 z-10 h-full rounded-md bg-indigo-400 transition-all duration-200 ease-out will-change-transform'
        left={highlight.startPosition}
        width={highlight.width}
        aria-label={`Selected breakpoint highlight`}
      />

      {/* Vertical separators ONLY at start and end of selected breakpoint - height matches timeline */}
      <Separator
        className='absolute -top-1.25 w-px bg-white transition-all duration-200'
        left={highlight.startPosition}
        height={timelineHeight + 10}
      />
      <Separator
        className='absolute -top-1.25 w-px bg-white transition-all duration-200'
        left={highlight.endPosition}
        height={timelineHeight + 10}
      />
    </>
  );
}
